const REALTIME_VOICES = new Set(['alloy', 'ash', 'ballad', 'cedar', 'coral', 'echo', 'marin', 'sage', 'shimmer', 'verse'])
const DEFAULT_REALTIME_VOICE = 'sage'

const DELTA_EVENTS = new Set([
  'response.output_text.delta',
  'response.text.delta',
  'response.audio_transcript.delta',
  'response.output_audio_transcript.delta'
])

const DONE_EVENTS = new Set([
  'response.output_text.done',
  'response.text.done',
  'response.audio_transcript.done',
  'response.output_audio_transcript.done'
])

const LOGGED_EVENT_SKIP = new Set([
  'response.audio.delta',
  'input_audio_buffer.append',
  'response.audio_transcript.delta',
  'response.output_audio_transcript.delta'
])

const MIC_GATE_RMS_THRESHOLD = 0.025
const MIC_GATE_REQUIRED_HITS = 2
const MIC_GATE_INTERVAL_MS = 50

export async function createQuestRealtimeSession({
  gameId,
  gameName,
  voiceEnabled,
  selectedVoice,
  onStatus,
  onTextDelta,
  onFinalText,
  onUserTranscript,
  onError
}) {
  const client = new QuestRealtimeClient({
    gameId,
    gameName,
    voiceEnabled,
    selectedVoice,
    onStatus,
    onTextDelta,
    onFinalText,
    onUserTranscript,
    onError
  })

  await client.start()
  return client
}

class QuestRealtimeClient {
  constructor({
    gameId,
    gameName,
    voiceEnabled,
    selectedVoice,
    onStatus,
    onTextDelta,
    onFinalText,
    onUserTranscript,
    onError
  }) {
    this.gameId = gameId
    this.gameName = gameName
    this.voiceEnabled = voiceEnabled
    this.selectedVoice = selectedVoice
    this.onStatus = onStatus || (() => {})
    this.onTextDelta = onTextDelta || (() => {})
    this.onFinalText = onFinalText || (() => {})
    this.onUserTranscript = onUserTranscript || (() => {})
    this.onError = onError || (() => {})

    this.pc = null
    this.dc = null
    this.localStream = null
    this.remoteAudio = null
    this.abortController = null
    this.sessionId = `quest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    this.starting = false
    this.activeText = ''
    this.pendingTurn = null
    this.audioContext = null
    this.micAnalyser = null
    this.micAnalyserData = null
    this.micGateTimer = null
    this.micGateHitCount = 0
    this.micTrack = null
    this.silentTrack = null
    this.audioSender = null
    this.micOpen = false
  }

  async start() {
    if (this.pc || this.starting) return
    this.starting = true
    this.onStatus('connecting')

    try {
      const contextResponse = await fetch('/api/quest/realtime/context', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: this.gameId,
          game_name: this.gameName
        })
      })
      if (!contextResponse.ok) {
        throw new Error(`Realtime context failed: ${contextResponse.status}`)
      }
      const { context_token: contextToken } = await contextResponse.json()

      this.pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      })

      this.pc.onconnectionstatechange = () => {
        const state = this.pc?.connectionState
        if (state) this.onStatus(state)
        if (state === 'failed' || state === 'disconnected') {
          this.onError(new Error(`Realtime connection ${state}`))
        }
      }

      if (this.voiceEnabled) {
        this.remoteAudio = document.createElement('audio')
        this.remoteAudio.autoplay = true
        document.body.appendChild(this.remoteAudio)
        this.pc.ontrack = (event) => {
          this.remoteAudio.srcObject = event.streams[0]
          this.remoteAudio.play().catch((error) => {
            console.warn('Quest Realtime audio play blocked:', error)
          })
        }

        this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        const tracks = this.localStream.getTracks()
        if (tracks.length === 0) {
          throw new Error('Microphone returned no audio tracks')
        }
        this.micTrack = this.localStream.getAudioTracks()[0]
        this.prepareMicrophoneGate()
        this.audioSender = this.pc.addTrack(this.silentTrack, new MediaStream([this.silentTrack]))
      } else {
        this.pc.addTransceiver('audio', { direction: 'recvonly' })
      }

      this.dc = this.pc.createDataChannel('oai-events')
      const dataChannelReady = new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          reject(new Error('Realtime data channel did not open'))
        }, 10000)

        this.dc.addEventListener('open', () => {
          window.clearTimeout(timeoutId)
          this.handleChannelOpen()
          resolve()
        }, { once: true })

        this.dc.addEventListener('error', () => {
          window.clearTimeout(timeoutId)
          reject(new Error('Realtime data channel error'))
        }, { once: true })
      })
      this.dc.addEventListener('message', event => this.handleEvent(JSON.parse(event.data)))
      this.dc.addEventListener('close', () => this.onStatus('closed'))
      this.dc.addEventListener('error', () => this.onError(new Error('Realtime data channel error')))

      const offer = await this.pc.createOffer()
      await this.pc.setLocalDescription(offer)

      if (!offer.sdp || offer.sdp.trim().length < 50) {
        throw new Error('WebRTC offer SDP is empty or malformed')
      }

      this.abortController = new AbortController()
      const voice = REALTIME_VOICES.has(this.selectedVoice) ? this.selectedVoice : DEFAULT_REALTIME_VOICE
      const sessionResponse = await fetch(`/voice-session/session?voice=${encodeURIComponent(voice)}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/sdp',
          'X-Quest-Realtime-Context': contextToken
        },
        body: offer.sdp,
        signal: this.abortController.signal
      })

      const answerSdp = await sessionResponse.text()
      if (!sessionResponse.ok) {
        throw new Error(answerSdp || `Realtime session failed: ${sessionResponse.status}`)
      }

      await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
      await dataChannelReady
      this.abortController = null
      this.starting = false
      this.onStatus('connected')
      this.logClientEvent('quest:session_connected', { voiceEnabled: this.voiceEnabled, voice })
    } catch (error) {
      this.close()
      this.onError(error)
      throw error
    }
  }

  handleChannelOpen() {
    this.sendEvent({
      type: 'session.update',
      session: {
        type: 'realtime',
        tool_choice: 'auto',
        tools: [
          {
            type: 'function',
            name: 'wait_for_user',
            description: 'Call when audio is silence, background noise, or speech not addressed to Quest.',
            parameters: {
              type: 'object',
              additionalProperties: false,
              properties: {},
              required: []
            }
          }
        ],
        audio: {
          input: {
            turn_detection: this.voiceEnabled
              ? {
                  type: 'server_vad',
                  threshold: 0.5,
                  prefix_padding_ms: 500,
                  silence_duration_ms: 1400
                }
              : null
          }
        }
      }
    })
    this.onStatus('ready')
  }

  sendText(text) {
    if (!text || this.dc?.readyState !== 'open') {
      return Promise.reject(new Error('Realtime session is not ready'))
    }
    if (this.pendingTurn) {
      return Promise.reject(new Error('Quest is still answering'))
    }

    this.activeText = ''
    this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text }]
      }
    })

    this.sendEvent({
      type: 'response.create'
    })

    return new Promise((resolve, reject) => {
      this.pendingTurn = { resolve, reject }
    })
  }

  sendEvent(event) {
    if (this.dc?.readyState === 'open') {
      this.dc.send(JSON.stringify(event))
      this.logEvent(event)
    }
  }

  setMicrophoneEnabled(enabled) {
    if (!this.voiceEnabled || !this.micTrack || !this.audioSender) return
    if (enabled) {
      this.startMicrophoneGate()
    } else {
      this.stopMicrophoneGate()
      this.closeMicrophoneGate()
    }
  }

  prepareMicrophoneGate() {
    this.audioContext = new AudioContext()
    const source = this.audioContext.createMediaStreamSource(this.localStream)
    this.micAnalyser = this.audioContext.createAnalyser()
    this.micAnalyser.fftSize = 1024
    this.micAnalyserData = new Float32Array(this.micAnalyser.fftSize)
    source.connect(this.micAnalyser)

    const silenceDestination = this.audioContext.createMediaStreamDestination()
    const gain = this.audioContext.createGain()
    gain.gain.value = 0
    const oscillator = this.audioContext.createOscillator()
    oscillator.connect(gain).connect(silenceDestination)
    oscillator.start()
    this.silentTrack = silenceDestination.stream.getAudioTracks()[0]
  }

  startMicrophoneGate() {
    if (this.micGateTimer) return
    this.micGateHitCount = 0
    this.logClientEvent('quest:mic_gate_armed', { threshold: MIC_GATE_RMS_THRESHOLD })
    this.audioContext?.resume?.().catch(() => {})
    this.micGateTimer = window.setInterval(() => {
      const rms = this.currentMicRms()
      if (rms >= MIC_GATE_RMS_THRESHOLD) {
        this.micGateHitCount += 1
      } else {
        this.micGateHitCount = 0
      }

      if (this.micGateHitCount >= MIC_GATE_REQUIRED_HITS) {
        this.openMicrophoneGate(rms)
      }
    }, MIC_GATE_INTERVAL_MS)
  }

  stopMicrophoneGate() {
    if (this.micGateTimer) {
      window.clearInterval(this.micGateTimer)
      this.micGateTimer = null
    }
    this.micGateHitCount = 0
  }

  currentMicRms() {
    if (!this.micAnalyser || !this.micAnalyserData) return 0
    this.micAnalyser.getFloatTimeDomainData(this.micAnalyserData)
    let sum = 0
    for (let i = 0; i < this.micAnalyserData.length; i += 1) {
      sum += this.micAnalyserData[i] * this.micAnalyserData[i]
    }
    return Math.sqrt(sum / this.micAnalyserData.length)
  }

  openMicrophoneGate(rms) {
    if (this.micOpen) return
    this.stopMicrophoneGate()
    this.audioSender.replaceTrack(this.micTrack)
    this.micOpen = true
    this.logClientEvent('quest:mic_gate_opened', { rms })
  }

  closeMicrophoneGate() {
    if (!this.audioSender || !this.silentTrack) return
    this.audioSender.replaceTrack(this.silentTrack)
    if (this.micOpen) {
      this.logClientEvent('quest:mic_gate_closed', {})
    }
    this.micOpen = false
  }

  logClientEvent(type, detail = {}) {
    this.logEvent({ type, sessionId: this.sessionId, gameId: this.gameId, gameName: this.gameName, ...detail })
  }

  logEvent(event) {
    if (LOGGED_EVENT_SKIP.has(event.type)) return
    fetch('/voice-session/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: this.sessionId,
        ts: Date.now(),
        event: {
          ...event,
          gameId: this.gameId,
          gameName: this.gameName
        }
      }),
      keepalive: true
    }).catch(() => {})
  }

  handleEvent(event) {
    this.logEvent(event)

    if (event.type === 'error') {
      const message = event.error?.message || 'Realtime error'
      const error = new Error(message)
      if (this.pendingTurn) {
        this.pendingTurn.reject(error)
        this.pendingTurn = null
      }
      this.onError(error)
      return
    }

    if (event.type === 'conversation.item.input_audio_transcription.delta' && event.transcript) {
      this.onUserTranscript(event.transcript, false)
      return
    }

    if (event.type === 'conversation.item.input_audio_transcription.delta') {
      const delta = event.delta || event.text || ''
      if (delta) this.onUserTranscript(delta, false)
      return
    }

    if (event.type === 'conversation.item.input_audio_transcription.completed' && event.transcript) {
      this.onUserTranscript(event.transcript, true)
      return
    }

    if (DELTA_EVENTS.has(event.type)) {
      const delta = event.delta || event.text || event.transcript || ''
      if (delta) {
        this.activeText += delta
        this.onTextDelta(this.activeText)
      }
      return
    }

    if (DONE_EVENTS.has(event.type)) {
      this.finalizeText(event.text || event.transcript || this.activeText)
      return
    }

    if (event.type === 'response.done') {
      const calls = (event.response?.output || []).filter(item => item.type === 'function_call')
      if (calls.some(call => call.name === 'wait_for_user')) {
        this.pendingTurn?.resolve('')
        this.pendingTurn = null
        this.activeText = ''
        return
      }
      if (this.activeText) {
        this.finalizeText(this.activeText)
      }
    }
  }

  finalizeText(text) {
    const finalText = (text || '').trim()
    if (!finalText) return
    this.onFinalText(finalText)
    if (this.pendingTurn) {
      this.pendingTurn.resolve(finalText)
      this.pendingTurn = null
    }
    this.activeText = ''
  }

  close() {
    this.starting = false
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    if (this.pendingTurn) {
      this.pendingTurn.reject(new Error('Realtime session closed'))
      this.pendingTurn = null
    }
    if (this.dc) {
      this.dc.close()
      this.dc = null
    }
    if (this.pc) {
      this.pc.close()
      this.pc = null
    }
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop())
      this.localStream = null
    }
    this.stopMicrophoneGate()
    if (this.silentTrack) {
      this.silentTrack.stop()
      this.silentTrack = null
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
    }
    this.micTrack = null
    this.audioSender = null
    this.micOpen = false
    if (this.remoteAudio) {
      this.remoteAudio.srcObject?.getTracks().forEach(track => track.stop())
      this.remoteAudio.srcObject = null
      this.remoteAudio.remove()
      this.remoteAudio = null
    }
    this.logClientEvent('quest:session_closed', {})
    this.onStatus('closed')
  }
}
